import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Eye, X } from "lucide-react";

export default function PolicyAppliedMastersEye({
  policyName = "",
  badges = [],
  policyDocumentPushed,
  darkMode = false,
}) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const items = Array.isArray(badges) && badges.length ? badges : ["No scope defined"];

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`inline-flex items-center justify-center p-1 transition-colors ${
          darkMode
            ? "text-[hsl(var(--ds-muted-fg))] hover:text-[hsl(var(--ds-accent-fg))]"
            : "text-[hsl(var(--ds-muted-fg))] hover:text-[hsl(var(--ds-primary))]"
        }`}
        title="View applied masters"
        aria-label="View applied masters"
      >
        <Eye size={16} strokeWidth={2} />
      </button>

      {open &&
        createPortal(
          <div
            className="fixed inset-0 z-[10007] flex items-center justify-center bg-black/45 p-4"
            onClick={() => setOpen(false)}
          >
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="applied-masters-title"
              className={`flex max-h-[80vh] w-[min(100%,420px)] flex-col overflow-hidden rounded-xl border shadow-2xl ${
                darkMode ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]" : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]"
              }`}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                className={`flex items-start justify-between gap-3 border-b px-4 py-3 ${
                  darkMode ? "border-[hsl(var(--ds-border))]" : "border-[hsl(var(--ds-border))]"
                }`}
              >
                <div>
                  <h4
                    id="applied-masters-title"
                    className={`text-sm font-semibold ${darkMode ? "text-gray-100" : "text-[hsl(var(--ds-fg))]"}`}
                  >
                    Applied masters
                  </h4>
                  {policyName ? (
                    <p className={`mt-0.5 text-xs ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"}`}>
                      {policyName}
                    </p>
                  ) : null}
                </div>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className={`rounded-full p-1 transition-colors ${
                    darkMode
                      ? "text-[hsl(var(--ds-muted-fg))] hover:bg-[hsl(var(--ds-muted))] hover:text-[hsl(var(--ds-fg))]"
                      : "text-[hsl(var(--ds-muted-fg))] hover:bg-[hsl(var(--ds-muted))] hover:text-[hsl(var(--ds-fg))]"
                  }`}
                  aria-label="Close"
                >
                  <X size={16} />
                </button>
              </div>

              <div className="flex-1 space-y-3 overflow-y-auto px-4 py-3">
                {typeof policyDocumentPushed === "boolean" && (
                  <div
                    className={`rounded-lg border px-3 py-2 text-xs ${
                      policyDocumentPushed
                        ? darkMode
                          ? "border-emerald-800 bg-emerald-950/40 text-emerald-200"
                          : "border-emerald-200 bg-emerald-50 text-emerald-800"
                        : darkMode
                          ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-muted-fg))]"
                          : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-muted-fg))]"
                    }`}
                  >
                    Policy document: {policyDocumentPushed ? "Pushed to employees" : "Not pushed"}
                  </div>
                )}

                <ul className="space-y-2">
                  {items.map((badge, idx) => (
                    <li
                      key={`${badge}-${idx}`}
                      className={`rounded-lg border px-3 py-2 text-xs ${
                        darkMode
                          ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-fg))]"
                          : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-fg))]"
                      }`}
                    >
                      {badge}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="flex justify-end border-t border-[hsl(var(--ds-border))] px-4 py-3">
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="rounded-md border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-4 py-2 text-sm text-[hsl(var(--ds-fg))] transition-colors hover:bg-[hsl(var(--ds-muted))]"
                >
                  Close
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
